import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

// 年度聚合函數
const aggregateYearly = (data, method = "sum") => {
  const yearly = {};
  
  data.forEach(({ date, value }) => {
    const year = date.slice(0, 4); // 取年份
    if (!yearly[year]) yearly[year] = [];
    yearly[year].push(value);
  });

  return Object.entries(yearly).map(([year, values]) => {
    let agg;
    if (method === "sum") {
      agg = values.reduce((a, b) => a + b, 0);
    } else {
      // 預設取平均
      agg = values.reduce((a, b) => a + b, 0) / values.length;
    }
    return { year, value: agg };
  });
};

const buildChartData = (filteredDates, tradeSeries, isYearly = false) => {
  if (isYearly) {
    // 年度聚合模式
    const years = [...new Set(filteredDates.map(date => date.slice(0, 4)))].sort();
    
    return years.map(year => {
      const row = { date: year };
      
      tradeSeries.forEach((s) => {
        // 計算該年度的總和
        const yearlySum = s.data.reduce((sum, value, index) => {
          const dateYear = filteredDates[index]?.slice(0, 4);
          return dateYear === year ? sum + (value || 0) : sum;
        }, 0);
        
        row[s.label] = yearlySum;
      });
      
      return row;
    });
  } else {
    // 月度顯示模式
    return filteredDates.map((date, i) => {
      const row = { date: date.slice(0, 7) }; // "2025-06-01" → "2025-06"
      tradeSeries.forEach((s) => {
        row[s.label] = s.data[i] ?? 0;
      });
      return row;
    });
  }
};

const TradeChart = ({ filteredDates, tradeSeries, tradeMode, range }) => {
  // 判斷是否為年度聚合模式（當 range 為 365*100+31 時）
  const isYearly = range === 365 * 100 + 31;
  
  const chartData = buildChartData(filteredDates, tradeSeries, isYearly);
  
  // 判断是否为综合模式（并列显示）
  const isCombinedMode = tradeMode === "combined";

  return (
    <ResponsiveContainer width="100%" height={600}>
      <BarChart
        data={chartData}
        margin={{ top: 30, right: 80, left: 80, bottom: 30 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          angle={-45}
          textAnchor="end"
          tick={{ fontSize: 12 }}
          height={80}
          label={{
            value: isYearly ? "年份" : "月份",
            position: "insideBottom",
            offset: -5
          }}
        />
        <YAxis
          tickFormatter={(v) => `$${(v / 1000000).toFixed(0)}M`} // 转换为百万美元显示
          label={{ 
            value: isYearly ? "年度金額 (百萬美元)" : "月度金額 (百萬美元)", 
            angle: -90, 
            position: "insideLeft",
            offset: -60, // 增加偏移量，让标签远离纵轴
            style: { textAnchor: 'middle' }
          }}
        />
        <Tooltip
          formatter={(value, name) => [
            `$${(Number(value) / 1000000).toFixed(2)}M`, // 转换为百万美元
            name
          ]}
          labelFormatter={(label) => `${isYearly ? '年份' : '日期'}: ${label}`}
          contentStyle={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #ccc',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        />
        <Legend 
          wrapperStyle={{ paddingTop: '20px' }}
        />

        {/* 根据模式决定是堆叠还是并列 */}
        {tradeSeries.map((s, index) => (
          <Bar
            key={s.label}
            dataKey={s.label}
            stackId={isCombinedMode ? undefined : (s.stack || "default")} // 综合模式不堆叠，其他模式堆叠
            name={s.label}
            fill={s.color || `hsl(${(index * 360) / tradeSeries.length}, 70%, 50%)`} // 如果没有颜色则自动生成
            maxBarSize={isCombinedMode ? 40 : undefined} // 综合模式限制柱子宽度
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default TradeChart;